import React, { useEffect, useState } from "react";
import { Client } from "@/bryntum-lib/models/Client";
import { Input } from "@/modern/components/ui/input";
import { Label } from "@/modern/components/ui/label";
import { Button } from "@/modern/components/ui/button";
import { Dialog, DialogFooter } from "@/modern/components/ui/dialog";
import { SharedModal, SharedModalHeader, SharedModalTitle } from "@/modern/components/shared-modal-component";
import { useForm, Controller } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { clientSchema, ClientFormData } from "@/react/schema/clientSchema";
import { toast } from "sonner";

interface ClientGeneralEditorProps {
  record: Client;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export const ClientGeneralEditor = ({ record, open, onOpenChange }: ClientGeneralEditorProps) => {
  const [isLoading, setIsLoading] = useState(false);

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors, isDirty }
  } = useForm<ClientFormData>({
    resolver: zodResolver(clientSchema),
    defaultValues: {
      name: record.name || "",
      website: record.website || "",
      address: record.address || ""
    }
  });

  // Reset form when dialog opens with fresh data
  useEffect(() => {
    if (open) {
      reset({
        name: record.name || "",
        website: record.website || "",
        address: record.address || ""
      });
    }
  }, [open, record, reset]);

  const onSubmit = async (data: ClientFormData) => {
    try {
      setIsLoading(true);

      Object.assign(record, data);

      onOpenChange(false);
    } catch (error) {
      console.error("Error saving client:", error);
      toast("Failed to save client. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = (shouldClose: boolean) => {
    if (!isLoading) {
      onOpenChange(shouldClose);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleClose} modal={false}>
      <SharedModal>
        <SharedModalHeader>
          <SharedModalTitle>General Settings</SharedModalTitle>
        </SharedModalHeader>

        <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col flex-1 min-h-0">
          <div className="overflow-y-auto px-6 space-y-6 flex-1 pb-6">
            {/* Info Message */}
            <div className="w-full py-2 border-0 border-l-4 border-solid border-[#e0e0e0] dark:border-[#333] pl-[6px] bg-[#fafafa] dark:bg-[#252525] dark:text-[#a5a5a5] text-sm">
              Clients can be customers, members, or any other stakeholder that tasks are performed for.
            </div>

            {/* Client Details Section */}
            <div className="space-y-4">
              {/* Client Name */}
              <div className="space-y-2">
                <Label htmlFor="name" className="text-sm font-medium">
                  Client Name *
                </Label>
                <Controller
                  name="name"
                  control={control}
                  render={({ field }) => (
                    <Input id="name" variant="background" placeholder="Please enter client name first" autoComplete="webauthn" {...field} className={errors.name ? "border-destructive" : ""} />
                  )}
                />
                {errors.name && <p className="text-sm text-destructive">{errors.name.message}</p>}
              </div>

              {/* Website Link */}
              <div className="space-y-2">
                <Label htmlFor="website" className="text-sm font-medium">
                  Website Link
                </Label>
                <Controller
                  name="website"
                  control={control}
                  render={({ field }) => (
                    <Input id="website" variant="background" placeholder="Enter website URL" autoComplete="off" {...field} className={errors.website ? "border-destructive" : ""} />
                  )}
                />
                {errors.website && <p className="text-sm text-destructive">{errors.website.message}</p>}
              </div>

              {/* Address */}
              <div className="space-y-2">
                <Label htmlFor="address" className="text-sm font-medium">
                  Address
                </Label>
                <Controller
                  name="address"
                  control={control}
                  render={({ field }) => <Input id="address" variant="background" placeholder="Enter address" autoComplete="off" {...field} className={errors.address ? "border-destructive" : ""} />}
                />
                {errors.address && <p className="text-sm text-destructive">{errors.address.message}</p>}
              </div>
            </div>
          </div>

          <DialogFooter className="flex-col items-start gap-3">
            {!record.isPhantom && (
              <div className="text-xs text-muted-foreground w-full">
                {record.creatorRecord && record.createdAt && (
                  <span>
                    Created by {record.creatorRecord.name} on {new Date(record.createdAt).toLocaleDateString("en-US", { month: "numeric", day: "numeric", year: "numeric" })}
                  </span>
                )}
                {record.updaterRecord && record.updatedAt && (
                  <>
                    {" | "}
                    <span>
                      General data changed by {record.updaterRecord.name} on {new Date(record.updatedAt).toLocaleDateString("en-US", { month: "numeric", day: "numeric", year: "numeric" })}
                    </span>
                  </>
                )}
              </div>
            )}
            <div className="flex justify-end gap-2 w-full">
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={isLoading}>
                Cancel
              </Button>
              <Button type="submit" disabled={isLoading || !isDirty}>
                {isLoading ? "Saving..." : "Save Changes"}
              </Button>
            </div>
          </DialogFooter>
        </form>
      </SharedModal>
    </Dialog>
  );
};
